import { useCallback, useEffect, useRef } from 'react';

import { BroadcastChannel, BroadcastChannelOptions } from 'broadcast-channel';
import semver from 'semver';

import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { normalizeVersion } from '@utils/misc';

export const BROADCAST_CHANNEL = {
  CHARACTER_UPDATE: 'character_update',
  CHARACTER_DELETE: 'character_delete',
  CHAT_ROOM_INACTIVE: 'chat_room_inactive',
  VERIFICATION_EVENT_CONFIRM: 'verification_event_confirm',
  PURCHASE_DAILY_FREE_PASS: 'purchase_daily_free_pass',
} as const;

export type BroadcastChannelName =
  (typeof BROADCAST_CHANNEL)[keyof typeof BROADCAST_CHANNEL];

export type UseBroadcastChannelOptions = Omit<BroadcastChannelOptions, 'node'>;

export function useBroadcastChannel<T>(
  channelName: BroadcastChannelName,
  onMessage?: (message: T) => void,
  options?: UseBroadcastChannelOptions
) {
  const { platform, os } = useGlobalContext();
  const isUnderVersion =
    platform === Platform.ios &&
    os &&
    !semver.gte(normalizeVersion(os), '15.4.0');

  const broadcastChannelRef = useRef<BroadcastChannel<T> | null>(null);
  const onMessageRef = useRef<((message: T) => void) | null>(null);

  const handlePostMessage = useCallback((message: T) => {
    if (platform === Platform.ios && isUnderVersion) {
      return;
    }
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage(message);
    }
  }, []);

  useEffect(() => {
    if (platform === Platform.ios && isUnderVersion) {
      return;
    }
    if (onMessage) {
      onMessageRef.current = onMessage;
    }
  }, [onMessage]);

  useEffect(() => {
    if (platform === Platform.ios && isUnderVersion) {
      return;
    }

    let mounted = true;
    const channel = new BroadcastChannel<T>(channelName, options);

    const handleMessage = (message: T) => {
      if (!mounted) {
        return;
      }
      onMessageRef.current?.(message);
    };

    channel.onmessage = handleMessage;
    broadcastChannelRef.current = channel;

    return () => {
      mounted = false;
      broadcastChannelRef.current = null;
      channel.close();
    };
  }, [channelName, options]);

  return { postChannelMessage: handlePostMessage };
}
